import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { FileArrowUp, UploadSimple, Warning } from '@phosphor-icons/react';

export default function FirmwareFlasher({
    connectionMode, firmwareFile, setFirmwareFile,
    flashProgress, flashMessage, isFlashing,
    handleFirmwareSelect, flashFirmware,
    selectedBrand, selectedModel,
}) {
    return (
        <Card className="glass-card">
            <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-plasma-red/10 flex items-center justify-center">
                        <FileArrowUp size={24} className="text-plasma-red" />
                    </div>
                    <div>
                        <h3 className="font-display text-lg">Firmware Flasher</h3>
                        <p className="text-xs text-muted-foreground">Upload and flash firmware to your controller via Web Serial</p>
                    </div>
                </div>

                {connectionMode !== 'serial' ? (
                    <div className="p-4 rounded-xl bg-boltz-gold/5 border border-boltz-gold/20 text-center">
                        <Warning size={32} className="text-boltz-gold mx-auto mb-2" />
                        <p className="text-sm text-boltz-gold font-display">UART Connection Required</p>
                        <p className="text-xs text-muted-foreground mt-1">
                            Firmware flashing requires a direct USB/UART serial connection. Go to the Connect tab and connect via UART first.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="border-2 border-dashed border-white/10 rounded-xl p-6 text-center hover:border-primary/30 transition-colors" data-testid="firmware-dropzone">
                            <input type="file" accept=".bin,.hex,.fw,.img" onChange={handleFirmwareSelect}
                                className="hidden" id="firmware-input" data-testid="firmware-file-input" />
                            <label htmlFor="firmware-input" className="cursor-pointer">
                                <UploadSimple size={36} className="text-muted-foreground mx-auto mb-2" />
                                <p className="text-sm font-display text-muted-foreground">
                                    {firmwareFile ? firmwareFile.name : 'Click to select firmware file'}
                                </p>
                                <p className="text-[10px] text-muted-foreground/60 mt-1">Supported: .bin .hex .fw .img</p>
                            </label>
                        </div>

                        {firmwareFile && (
                            <div className="space-y-3">
                                {[
                                    ['File:', firmwareFile.name],
                                    ['Size:', `${(firmwareFile.size / 1024).toFixed(1)} KB`],
                                    ['Target:', `${selectedBrand?.brand} ${selectedModel}`],
                                ].map(([label, val]) => (
                                    <div key={label} className="flex items-center justify-between text-xs p-3 rounded-lg bg-white/5">
                                        <span className="text-muted-foreground">{label}</span>
                                        <span className="font-mono">{val}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {(isFlashing || flashProgress > 0) && (
                            <div className="space-y-2">
                                <Progress value={flashProgress} className="h-3" />
                                <p className="text-xs text-center font-mono text-muted-foreground" data-testid="flash-message">{flashMessage}</p>
                            </div>
                        )}

                        <Button onClick={flashFirmware} disabled={!firmwareFile || isFlashing}
                            className="w-full bg-plasma-red text-white font-display hover:bg-plasma-red/80"
                            data-testid="flash-firmware-btn">
                            <FileArrowUp size={18} className="mr-2" />
                            {isFlashing ? 'Flashing...' : 'Flash Firmware'}
                        </Button>

                        <div className="flex items-start gap-2 p-3 rounded-lg bg-plasma-red/5 border border-plasma-red/20">
                            <Warning size={14} className="text-plasma-red shrink-0 mt-0.5" />
                            <p className="text-[10px] text-plasma-red/80">
                                Flashing incorrect firmware can brick your controller. Ensure the firmware matches your exact controller model. Do not disconnect power or USB during flashing.
                            </p>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
